import React, { useEffect, useState, useMemo } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Sliders, X } from "lucide-react";
import Layout from "@/components/Layout";
import ProductCard from "@/components/ProductCard";
import { ScrollFade, StaggerGroup, StaggerItem, HeroStaggerText } from "@/lib/animations";
import api from "@/lib/api";

const SORTS = [
  { id: "newest", label: "Newest" },
  { id: "price_asc", label: "Price: Low → High" },
  { id: "price_desc", label: "Price: High → Low" },
  { id: "name", label: "Name A-Z" },
];

export default function Shop() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const sort = searchParams.get("sort") || "newest";
  const search = searchParams.get("q") || "";
  const minPrice = searchParams.get("min") || "";
  const maxPrice = searchParams.get("max") || "";

  const activeCategory = slug || "all";

  useEffect(() => {
    api.get("/categories").then(r => setCategories(r.data || []));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (activeCategory !== "all") params.set("category", activeCategory);
    if (sort) params.set("sort", sort);
    if (search) params.set("search", search);
    if (minPrice) params.set("min_price", minPrice);
    if (maxPrice) params.set("max_price", maxPrice);
    api.get(`/products?${params.toString()}`).then(r => {
      setProducts(r.data || []);
      setLoading(false);
    });
  }, [activeCategory, sort, search, minPrice, maxPrice]);

  const heading = useMemo(() => {
    if (activeCategory === "all") return "Shop All";
    return categories.find(c => c.slug === activeCategory)?.name || activeCategory;
  }, [activeCategory, categories]);

  const tagline = useMemo(() => {
    return categories.find(c => c.slug === activeCategory)?.tagline || "Crafted essentials. Considered details.";
  }, [activeCategory, categories]);

  function setParam(key, val) {
    const next = new URLSearchParams(searchParams);
    if (val) next.set(key, val); else next.delete(key);
    setSearchParams(next);
  }

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-[#163A2F] text-[#F8F6F0] grain relative pt-16 pb-20 md:pt-24 md:pb-28">
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="overline text-[#C9A227]">Catalogue</div>
          <HeroStaggerText
            text={heading}
            className="mt-3 text-5xl md:text-7xl font-extralight tracking-tight"
          />
          <p className="mt-4 max-w-xl text-[#F8F6F0]/70 font-light">{tagline}</p>
        </div>
      </section>

      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 py-10">
        {/* Category chips */}
        <ScrollFade>
          <div className="flex flex-wrap gap-2 md:gap-3 mb-6">
            <Link
              to="/shop"
              data-testid="category-filter-all"
              className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border transition-colors ${
                activeCategory === "all"
                  ? "bg-[#163A2F] text-white border-[#163A2F]"
                  : "bg-transparent text-[#163A2F] border-[#163A2F]/30 hover:border-[#163A2F]"
              }`}
            >
              All
            </Link>
            {categories.map(c => (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                data-testid={`category-filter-${c.slug}`}
                className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border transition-colors ${
                  activeCategory === c.slug
                    ? "bg-[#163A2F] text-white border-[#163A2F]"
                    : "bg-transparent text-[#163A2F] border-[#163A2F]/30 hover:border-[#163A2F]"
                }`}
              >
                {c.name}
              </Link>
            ))}
          </div>
        </ScrollFade>

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row justify-between gap-4 mb-10 border-t border-b hairline py-4">
          <div className="flex items-center gap-3 text-[#163A2F]">
            <button
              onClick={() => setFiltersOpen(v => !v)}
              data-testid="filters-toggle"
              className="flex items-center gap-2 text-xs uppercase tracking-[0.2em]"
            >
              <Sliders className="w-4 h-4" /> Filters
            </button>
            <span className="text-xs text-[#163A2F]/50">{products.length} pieces</span>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="text" placeholder="Search jackets…" defaultValue={search}
              data-testid="search-input"
              onChange={(e) => setParam("q", e.target.value)}
              className="px-4 py-2 bg-transparent border border-[#163A2F]/20 text-sm w-full sm:w-64 focus:outline-none focus:border-[#C9A227]"
            />
            <select
              value={sort} onChange={(e) => setParam("sort", e.target.value)}
              data-testid="sort-select"
              className="px-3 py-2 bg-transparent border border-[#163A2F]/20 text-sm text-[#163A2F]"
            >
              {SORTS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
        </div>

        {/* Inline price filter */}
        {filtersOpen && (
          <div className="bg-[#163A2F]/[0.03] border hairline p-5 mb-8 flex flex-wrap items-center gap-4">
            <div className="overline text-[#163A2F]">Price (₹)</div>
            <input type="number" placeholder="Min" value={minPrice} onChange={(e) => setParam("min", e.target.value)} data-testid="price-min" className="px-3 py-2 border hairline w-28 text-sm bg-transparent" />
            <span>—</span>
            <input type="number" placeholder="Max" value={maxPrice} onChange={(e) => setParam("max", e.target.value)} data-testid="price-max" className="px-3 py-2 border hairline w-28 text-sm bg-transparent" />
            <button onClick={() => { setParam("min", ""); setParam("max", ""); }} className="text-xs underline text-[#163A2F]/70">Clear</button>
            <button onClick={() => setFiltersOpen(false)} className="ml-auto text-[#163A2F]"><X className="w-4 h-4" /></button>
          </div>
        )}

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-[4/5] bg-[#163A2F]/5 animate-pulse" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20 text-[#163A2F]/60">No products found. Try a different filter.</div>
        ) : (
          <StaggerGroup className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
            {products.map((p, i) => (
              <StaggerItem key={p.id}><ProductCard product={p} index={i} /></StaggerItem>
            ))}
          </StaggerGroup>
        )}
      </div>
    </Layout>
  );
}
